import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { groupService } from '../../services/group.service';
import { userService } from '../../services/user.service';
import { PublicUser } from '../../types/user.types';
import { SurfaceCard, CardRow } from '../ui/SurfaceCard';
import { InitialsAvatar } from '../ui/InitialsAvatar';
import { StatusPill } from '../ui/StatusPill';

interface PreviewMember {
  userId: string;
  role: 'admin' | 'member';
  // Undefined when the user lookup failed; rendered as a translated placeholder.
  name?: string;
}

interface GroupMembersPreviewProps {
  groupId: string;
  isAdmin: boolean;
  totalMembers: number;
  // Changing this value reloads the preview (after the full members dialog closes).
  refreshKey: number;
  onViewAll: () => void;
}

const PREVIEW_SIZE = 5;

// Compact first-page member list; the full management UI opens from "View all members".
export const GroupMembersPreview = ({ groupId, isAdmin, totalMembers, refreshKey, onViewAll }: GroupMembersPreviewProps) => {
  const { t } = useTranslation('groups');
  const [members, setMembers] = useState<PreviewMember[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const response = await groupService.getGroupMembers(groupId, 1, PREVIEW_SIZE);
        const raw: { userId: string; role: 'admin' | 'member' }[] = response.data.members || [];
        const users = await Promise.all(
          raw.map(m => userService.getUserById(m.userId).then(r => r.data as PublicUser).catch(() => null))
        );
        if (cancelled) return;
        setMembers(raw.map((m, i) => ({ userId: m.userId, role: m.role, name: users[i]?.displayName || undefined })));

        if (isAdmin) {
          const pending = await groupService.getPendingJoinRequests(groupId);
          if (!cancelled) setPendingCount((pending.data || []).length);
        }
      } catch (err) {
        console.error('Error loading members preview:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [groupId, isAdmin, refreshKey]);

  return (
    <SurfaceCard
      title={t('preview.title')}
      bodyPadding={0}
      action={<Box sx={{ fontSize: 13, color: 'var(--muted)' }}>{totalMembers}</Box>}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        members.map(m => {
          const name = m.name || t('preview.unknownMember');
          return (
            <CardRow key={m.userId} sx={{ p: '10px 20px', gap: 1.25 }}>
              <InitialsAvatar name={name} size={30} />
              <Box sx={{ flex: 1, minWidth: 0, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</Box>
              <Box component="span" sx={{ fontSize: 12, color: 'var(--muted)' }}>{t(`roles.${m.role}`)}</Box>
            </CardRow>
          );
        })
      )}
      <CardRow sx={{ p: '12px 20px', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Button variant="text" size="small" onClick={onViewAll} sx={{ p: 0, minWidth: 0, fontSize: 13 }}>
          {isAdmin ? t('preview.manage') : t('preview.viewAll')}
        </Button>
        {isAdmin && pendingCount > 0 && <StatusPill tone="warn" label={t('preview.pending', { count: pendingCount })} />}
      </CardRow>
    </SurfaceCard>
  );
};
